import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dumbbell, Plus, Trash2, Pencil, Loader2, Check, X, GripVertical, Upload, FileSpreadsheet, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const blankEx = () => ({ name: '', sets: '', reps: '', rest: '', notes: '' });
const blankProgram = () => ({ title: '', notes: '', sessions: [{ name: 'Séance 1', exercises: [blankEx()] }] });

export const CoachWorkoutEditor = ({ email, csrfToken }) => {
  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });
  const jsonHeaders = { 'Content-Type': 'application/json' };
  
  const [programs, setPrograms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    fetch(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/programs`, { credentials: 'include' })
      .then(r => r.json()).then(d => setPrograms(d.programs || [])).catch(() => {});
  }, [email]);
  useEffect(() => { load(); }, [load]);

  const upd = (mut) => setEditing(e => { const c = JSON.parse(JSON.stringify(e)); mut(c); return c; });

  const save = () => {
    if (!editing.title.trim()) { toast.error('Le titre est requis.'); return; }
    setBusy(true);
    const isNew = !editing.program_id;
    const url = isNew
      ? `${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/programs`
      : `${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/programs/${editing.program_id}`;
    fetchWithCsrf(url, { method: isNew ? 'POST' : 'PUT', headers: jsonHeaders, body: JSON.stringify({ title: editing.title, notes: editing.notes, sessions: editing.sessions }) })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => { setEditing(null); load(); toast.success('Programme enregistré !'); })
      .catch(() => toast.error("Échec de l'enregistrement."))
      .finally(() => setBusy(false));
  };

  const del = (id) => {
    if (!window.confirm('Supprimer ce programme ?')) return;
    fetchWithCsrf(`${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/programs/${id}`, { method: 'DELETE' })
      .then(() => { load(); toast.success('Programme supprimé.'); });
  };

  // ── Upload Excel ────────────────────────────────────────────────────────────
  const handleExcelUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 5 Mo)'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetchWithCsrf(
        `${BACKEND_URL}/api/admin/clients/${encodeURIComponent(email)}/programs/upload-excel`,
        { method: 'POST', body: formData }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur upload');
      }
      const data = await res.json();
      load();
      toast.success(`Programme "${data.program?.title}" importé (${data.sessions_count} séances) !`);
    } catch (e) {
      toast.error(e.message || 'Erreur import Excel');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (editing) {
    return (
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-4 mt-5" data-testid="coach-program-editor">
        <input value={editing.title} onChange={(e) => upd((c) => { c.title = e.target.value; })} placeholder="Titre du programme (ex: Prise de masse - Bloc 1)"
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-2.5 text-white font-bold focus:border-primary focus:outline-none mb-3" data-testid="program-title-input" />
        <textarea value={editing.notes} onChange={(e) => upd((c) => { c.notes = e.target.value; })} placeholder="Notes générales (fréquence, repos, conseils…)" rows={2}
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:border-primary focus:outline-none mb-4" />
        {editing.sessions.map((s, si) => (
          <div key={si} className="rounded-xl border border-white/10 p-3 mb-3 bg-zinc-800/40">
            <div className="flex items-center gap-2 mb-2">
              <GripVertical className="w-4 h-4 text-zinc-600" />
              <input value={s.name} onChange={(e) => upd((c) => { c.sessions[si].name = e.target.value; })} placeholder="Nom de la séance"
                className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-semibold focus:border-primary focus:outline-none" data-testid="session-name-input" />
              <button onClick={() => upd((c) => { c.sessions.splice(si, 1); })} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {s.exercises.map((ex, ei) => (
                <div key={ei} className="rounded-lg bg-zinc-900/40 border border-white/5 p-2 space-y-1.5">
                  <div className="grid grid-cols-12 gap-1.5 items-center">
                    <input value={ex.name} onChange={(e) => upd((c) => { c.sessions[si].exercises[ei].name = e.target.value; })} placeholder="Exercice" className="col-span-4 bg-zinc-900 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs focus:border-primary focus:outline-none" data-testid="exercise-name-input" />
                    <input value={ex.sets} onChange={(e) => upd((c) => { c.sessions[si].exercises[ei].sets = e.target.value; })} placeholder="Séries" className="col-span-2 bg-zinc-900 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:border-primary focus:outline-none" />
                    <input value={ex.reps} onChange={(e) => upd((c) => { c.sessions[si].exercises[ei].reps = e.target.value; })} placeholder="Reps" className="col-span-2 bg-zinc-900 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:border-primary focus:outline-none" />
                    <input value={ex.rest} onChange={(e) => upd((c) => { c.sessions[si].exercises[ei].rest = e.target.value; })} placeholder="Repos" className="col-span-2 bg-zinc-900 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:border-primary focus:outline-none" />
                    <button onClick={() => upd((c) => { c.sessions[si].exercises.splice(ei, 1); })} className="col-span-2 text-zinc-500 hover:text-red-400 flex justify-center"><X className="w-4 h-4" /></button>
                  </div>
                  <input value={ex.notes} onChange={(e) => upd((c) => { c.sessions[si].exercises[ei].notes = e.target.value; })} placeholder="Notes (tempo, conseils…)" className="w-full bg-zinc-900 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs focus:border-primary focus:outline-none" />
                </div>
              ))}
            </div>
            <button onClick={() => upd((c) => { c.sessions[si].exercises.push(blankEx()); })} className="mt-2 text-xs text-primary hover:text-pink-400 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Exercice</button>
          </div>
        ))}
        <button onClick={() => upd((c) => { c.sessions.push({ name: `Séance ${c.sessions.length + 1}`, exercises: [blankEx()] }); })}
          className="text-sm text-zinc-300 border border-white/10 rounded-lg px-3 py-1.5 hover:border-primary/50 inline-flex items-center gap-1.5 mb-4"><Plus className="w-3.5 h-3.5" /> Ajouter une séance</button>
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className="px-4 py-2 rounded-lg font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50 flex items-center gap-2 text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
          </button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-300 border border-white/10 hover:text-white">Annuler</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-white/10 rounded-2xl p-4 mt-5" data-testid="coach-programs">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="flex items-center gap-2 font-bold text-white text-sm"><Dumbbell className="w-4 h-4 text-primary" /> Programmes de musculation</h3>
        <div className="flex gap-2">
          {/* Upload Excel */}
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" id={`excel-upload-${email}`}
            onChange={e => handleExcelUpload(e.target.files?.[0])} />
          <label htmlFor={`excel-upload-${email}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600/80 hover:bg-emerald-600 cursor-pointer transition-colors">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
            {uploading ? 'Import…' : 'Import Excel'}
          </label>
          <button onClick={() => setEditing(blankProgram())} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform" data-testid="new-program-btn">
            <Plus className="w-3.5 h-3.5" /> Nouveau
          </button>
        </div>
      </div>
      {programs.length === 0 ? (
        <div className="text-center py-6">
          <FileSpreadsheet className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm">Aucun programme. Importez un Excel ou créez-en un manuellement.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {programs.map((p) => (
            <div key={p.program_id} className="flex items-center justify-between gap-3 rounded-xl bg-zinc-800/50 border border-white/10 px-3 py-2.5" data-testid="program-row">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white truncate">{p.title}</p>
                  {p.excel_parsed && <span className="px-1.5 py-0.5 rounded text-[9px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-bold">EXCEL</span>}
                </div>
                <p className="text-xs text-zinc-500">{(p.sessions || []).length} séance(s) · {new Date(p.created_at).toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setEditing(JSON.parse(JSON.stringify(p)))} className="p-1.5 text-zinc-400 hover:text-primary" data-testid="edit-program-btn"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p.program_id)} className="p-1.5 text-zinc-400 hover:text-red-400" data-testid="delete-program-btn"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoachWorkoutEditor;

